// InvoiceAccountAssignService — operator-driven correction of a RECEIVED invoice's
// missing seller_bank_account from a company_bank_accounts registry row.
//
// Browser-invoked (no edge function): the write to `invoices.seller_bank_account`
// plus the accompanying `invoice_ksef_finding` audit trail happen directly from the
// FE via this service + InvoiceDao / CompanyBankAccountDao / ShadowDao. Only an
// invoice that is genuinely missing its seller account is eligible, and only a
// registry row that has already cleared a trust bar (payment_verified /
// operator_verified / whitelisted) AND belongs to the invoice's own counterparty
// company can be assigned — an operator cannot use this path to staple an
// unverified or foreign account onto an invoice.
//
// Deno-compatible: explicit .ts extensions, no Node-only APIs.

import type { InvoiceDao } from './InvoiceDao.ts';
import type { CompanyBankAccountDao } from '../mbank/dao/CompanyBankAccountDao.ts';
import type { ShadowDao } from '../fakturownia/shadow/ShadowDao.ts';
import { canonicalAccountNumber } from '../../utils/iban.ts';
import { isInvoicePaid } from './paymentState.ts';

/** Trust tiers a registry row must have reached before an operator can assign it
 *  straight onto an invoice — the same payable set the payment/hold paths trust,
 *  plus 'whitelisted' (picking it from the registry IS the verification act).
 *  Deliberately excludes 'unverified', 'rejected', and 'superseded'.
 *  Exported: the picker UI (InvoiceChooseAccountForm) drives its disabled
 *  state from the SAME set — the service stays the enforcement point. */
export const ASSIGNABLE_STATUSES = new Set(['payment_verified', 'operator_verified', 'whitelisted']);

export interface InvoiceAccountAssignOptions {
  /** Opt in to REPLACING an account the invoice already states. Off by default:
   *  `assign` stays a fill-only operation, so no caller can silently overwrite a
   *  money-routing field it merely meant to populate. */
  replace?: boolean;
}

export interface InvoiceAccountAssignResult {
  invoiceId: number;
  /** Canonical form actually written (see canonicalAccountNumber). */
  accountNumber: string;
  /** False when the invoice has no external_id (never synced to/from Fakturownia):
   *  the seller_bank_account write still lands locally, but no KSeF finding audit
   *  trail is opened — there is nothing on the Fakturownia/KSeF side to reconcile it
   *  against. */
  findingCreated: boolean;
}

export class InvoiceAccountAssignService {
  constructor(
    private readonly invoiceDao: InvoiceDao,
    private readonly companyBankDao: CompanyBankAccountDao,
    private readonly shadowDao: ShadowDao,
  ) {}

  /**
   * Assign a verified registry bank account to a received invoice that is missing
   * its seller_bank_account.
   *
   * Flow:
   *   1. Load the invoice. Must exist and be `direction: 'received'`. It must
   *      carry no seller_bank_account UNLESS `opts.replace` is set, in which case
   *      the invoice must also still be unpaid (see below).
   *   2. Load the registry row. Must exist, belong to the invoice's counterparty
   *      company, and sit at an assignable trust tier.
   *   3. Write the canonicalized account number onto the invoice.
   *   4. When the invoice has an external_id, open an OPEN invoice_ksef_finding
   *      (field: 'seller_bank_account') so the correction surfaces in the same
   *      audit trail as every other KSeF-driven bank-account fix. Skipped (local
   *      write only) for an invoice that was never synced.
   */
  async assign(
    invoiceId: number,
    registryAccountId: number,
    opts: InvoiceAccountAssignOptions = {},
  ): Promise<InvoiceAccountAssignResult> {
    const invoice = await this.invoiceDao.getForBankAccountAssign(invoiceId);
    if (invoice == null) {
      throw new Error(`Faktura o id ${invoiceId} nie istnieje`);
    }
    if (invoice.direction !== 'received') {
      throw new Error('Numer konta bankowego można przypisać wyłącznie do faktury kosztowej (otrzymanej)');
    }
    const previousAccountNumber =
      invoice.seller_bank_account != null && invoice.seller_bank_account.trim() !== ''
        ? invoice.seller_bank_account
        : null;
    if (previousAccountNumber != null && !opts.replace) {
      throw new Error('Faktura ma już przypisany numer konta bankowego sprzedawcy');
    }
    // Once money has moved, the stated account is a historical fact — it is what
    // the bank operation reconciles against. Correcting it after the fact would
    // misrepresent where the payment actually went, so a REPLACEMENT stops here
    // (filling an empty account stays allowed: nothing was routed off it).
    if (previousAccountNumber != null && isInvoicePaid(invoice)) {
      throw new Error('Nie można zmienić rachunku na opłaconej fakturze');
    }
    if (previousAccountNumber != null && invoice.payment_status === 'partial') {
      throw new Error('Nie można zmienić rachunku na częściowo opłaconej fakturze');
    }

    const account = await this.companyBankDao.getForAssignment(registryAccountId);
    if (account == null) {
      throw new Error(`Konto bankowe o id ${registryAccountId} nie istnieje`);
    }
    if (account.company_id !== invoice.seller_company_id) {
      throw new Error('Wybrane konto bankowe nie należy do kontrahenta tej faktury');
    }
    if (!account.verification_status || !ASSIGNABLE_STATUSES.has(account.verification_status)) {
      throw new Error(
        'Wybrane konto bankowe nie osiągnęło wymaganego poziomu zaufania (wymagane: zweryfikowane płatnością, przez operatora lub whitelisted)',
      );
    }
    // A row can sit at 'whitelisted' tier while a LATER KAS check answered NO
    // (the two columns move independently). An explicit negative always blocks
    // assignment — same rule the resolver's registry cross-match applies.
    if (account.white_list_status === false) {
      throw new Error('Wybrane konto bankowe ma negatywny wynik weryfikacji na białej liście VAT');
    }

    const canonical = canonicalAccountNumber(account.account_number);
    if (canonical == null) {
      throw new Error('Nie udało się znormalizować numeru konta bankowego');
    }
    // Same account in a different display form is a no-op — refuse it rather than
    // churn a finding (and an FA PUT) that changes nothing.
    if (previousAccountNumber != null
        && canonicalAccountNumber(previousAccountNumber) === canonical) {
      throw new Error('Ten rachunek jest już przypisany do faktury');
    }

    // Finding FIRST, invoice second: the finding upsert is idempotent (open
    // (external_id, field) pair), so if the invoice write then fails the whole
    // call can simply be retried — whereas invoice-first with a failing finding
    // write would leave the money-routing field changed with no Fakturownia
    // audit trail and no way to re-run (the invoice would no longer be
    // account-less). Both writes verify their effect: RLS filters silently
    // (UPDATE 0 rows, no error), so an unverified write must throw here rather
    // than let the UI report success.
    let findingCreated = false;
    if (invoice.external_system_id) {
      await this.shadowDao.upsertFinding({
        invoice_id: invoiceId,
        external_id: invoice.external_system_id,
        field: 'seller_bank_account',
        fa_value: previousAccountNumber,
        ksef_value: canonical,
        status: 'open',
        attempts: 0,
        escalate_after: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // +1 day
        helpdesk_request_id: null,
      });
      findingCreated = true;
    }

    await this.invoiceDao.setSellerBankAccount(invoiceId, canonical);

    return { invoiceId, accountNumber: canonical, findingCreated };
  }
}
